import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import ConfirmDialog from './ConfirmDialog';
import './CategoryManager.css';


const BusinessManager = ({ onClose }) => {
  const { user, authFetch, refreshUser, userAccounts, fetchUserAccounts, activeAccountId } = useAuth();
  
  // Determinando qual conta gerenciar:
  const targetAccountId = activeAccountId || user?.business_id;
  const targetAccount = userAccounts?.find(a => a.account_id === targetAccountId);
  
  const businessName = targetAccount?.name || user?.nome_business || 'Business';
  const isOwner = targetAccount ? targetAccount.role === 'owner' : user?.is_business_owner;

  const [activeTab, setActiveTab] = useState('info'); // info | invites
  
  // States - Info/Business Creation
  const [newBusinessName, setNewBusinessName] = useState('');
  const [emailToInvite, setEmailToInvite] = useState('');
  const [members, setMembers] = useState([]);
  const [myInvites, setMyInvites] = useState([]);
  
  // UI States
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [confirmRemove, setConfirmRemove] = useState({
    show: false,
    memberId: null,
    isSelf: false,
    title: '',
    message: '',
    confirmWord: ''
  });


  
  useEffect(() => {
    fetchData();
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Puxar convites endereçados a mim
      const rInvites = await authFetch('/api/business/invites');
      if (rInvites.ok) setMyInvites(await rInvites.json());

      // Puxar membros do meu business, se eu tiver business alvo
      if (targetAccountId) {
        const rMembers = await authFetch(`/api/business/members?account_id=${targetAccountId}`);
        if (rMembers.ok) setMembers(await rMembers.json());
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBusiness = async (e) => {
    e.preventDefault();
    setMsg('');
    if (!newBusinessName.trim()) return;
    setLoading(true);

    try {
      const res = await authFetch('/api/business', {
        method: 'POST',
        body: JSON.stringify({ nome_business: newBusinessName }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || data.error);
      
      setMsg('✅ Conta Business criada!');
      await fetchUserAccounts();
      await refreshUser();
      setTimeout(() => setMsg(''), 3000);
    } catch (err) {
      setMsg('❌ ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setMsg('');
    if (!emailToInvite) return;
    setLoading(true);

    try {
      const res = await authFetch('/api/business/invite', {
        method: 'POST',
        body: JSON.stringify({ email_convidado: emailToInvite, account_id: targetAccountId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || data.error);
      
      setMsg('✅ Convite Enviado!');
      setEmailToInvite('');
    } catch (err) {
      setMsg('❌ ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRespond = async (inviteId, action) => {
    setLoading(true);
    try {
      const res = await authFetch(`/api/business/invites/${inviteId}/respond`, {
        method: 'POST',
        body: JSON.stringify({ action }), // 'aceitar' ou 'recusar'
      });
      if (!res.ok) throw new Error('Erro ao processar');
      
      if (action === 'aceitar') {
        // [NOVO] Conta adicionada (nao substitui mais a conta atual)
        alert('Nova conta business adicionada com sucesso!');
        await fetchUserAccounts();
        await refreshUser();
        onClose();
      } else {
        await fetchData();
      }
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const [isEditingName, setIsEditingName] = useState(false);
  const [editBusinessName, setEditBusinessName] = useState('');

  const handleRenameBusiness = async () => {
    if (!editBusinessName.trim() || editBusinessName === user.nome_business) {
      setIsEditingName(false);
      return;
    }
    setLoading(true);
    try {
      const res = await authFetch('/api/business/rename', {
        method: 'PUT',
        body: JSON.stringify({ novo_nome: editBusinessName, account_id: targetAccountId }),
      });
      if (!res.ok) throw new Error('Erro ao renomear');
      setMsg('✅ Business renomeado!');
      setIsEditingName(false);
      await refreshUser();
      fetchData();
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMember = (memberId, isSelf = false) => {
    
    setConfirmRemove({
      show: true,
      memberId,
      isSelf,
      title: isSelf ? (isOwner ? '🛑 ENCERRAR CONTA BUSINESS' : '⚠️ SAIR DA CONTA BUSINESS') : 'Remover Membro',
      message: isSelf 
        ? (isOwner 
            ? 'ATENÇÃO: Como você é o DONO, ao sair, a conta business será ENCERRADA e TODOS os lançamentos coletivos serão EXCLUÍDOS permanentemente para todos os membros.' 
            : 'Ao sair, você perderá acesso imediato às transações coletivas. Seus lançamentos feitos até agora permanecerão no grupo.')
        : 'Tem certeza que deseja remover este membro da conta business?',
      confirmWord: ''
    });
  };


  const executeRemoveMember = async () => {
    if (confirmRemove.isSelf && confirmRemove.confirmWord !== 'DeLeTe') return;
    
    const { memberId, isSelf } = confirmRemove;

    setLoading(true);
    try {
      const res = await authFetch(`/api/business/members/${memberId}?account_id=${targetAccountId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Erro ao remover membro');
      
      setMsg(isSelf ? 'Você saiu do business.' : 'Membro removido.');
      setTimeout(() => setMsg(''), 3000);
      
      setConfirmRemove({ ...confirmRemove, show: false });

      if (isSelf) {
        await refreshUser();
        onClose();
      } else {
        fetchData();
      }
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: '600px' }}>
        <div className="modal-header">
          <h2>💼 Minha Conta Business</h2>
          <button className="modal-close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="modal-tabs" style={{ display: 'flex', gap: '10px', padding: '10px 20px', borderBottom: '1px solid var(--border)' }}>
          <button 
            style={{ background: 'none', border: 'none', color: activeTab === 'info' ? 'var(--accent)' : 'var(--text-secondary)', fontWeight: 'bold', cursor: 'pointer' }}
            onClick={() => setActiveTab('info')}
          >
            Membros & Business
          </button>
          <button 
            style={{ background: 'none', border: 'none', color: activeTab === 'invites' ? 'var(--accent)' : 'var(--text-secondary)', fontWeight: 'bold', cursor: 'pointer', position: 'relative' }}
            onClick={() => setActiveTab('invites')}
          >
            Convites Recebidos
            {myInvites.length > 0 && (
              <span style={{ position: 'absolute', top: '-5px', right: '-15px', background: 'red', color: 'white', fontSize: '10px', padding: '2px 5px', borderRadius: '10px' }}>
                {myInvites.length}
              </span>
            )}
          </button>
        </div>

        <div className="modal-body" style={{ padding: '20px' }}>
          {msg && <div style={{ padding: '10px', background: 'var(--btn-secondary-bg)', borderRadius: '6px', marginBottom: '15px', color: 'var(--text-primary)' }}>{msg}</div>}

          {/* TAB 1: MEU BUSINESS */}
          {activeTab === 'info' && (
            <div>
              {!targetAccountId ? (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <p style={{ color: 'var(--text-secondary)', marginBottom: '15px' }}>Você ainda não pertence a nenhuma Conta Business compartilhada.</p>
                  
                  <div style={{ background: 'var(--bg-primary)', padding: '15px', borderRadius: '8px', border: '1px solid var(--border)' }}>
                    <h4>Criar Nova Conta Business</h4>
                    <form onSubmit={handleCreateBusiness} style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                      <input 
                        type="text" 
                        value={newBusinessName}
                        onChange={e => setNewBusinessName(e.target.value)}
                        placeholder="Nome (Ex: Walco Business)" 
                        className="form-control" 
                        required 
                      />
                        <button type="submit" className="modal-btn modal-btn-confirm" disabled={loading}>Ativar Business</button>
                    </form>
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    {isEditingName ? (
                      <div style={{ display: 'flex', gap: '8px', flex: 1 }}>
                        <input 
                          type="text" 
                          value={editBusinessName} 
                          onChange={e => setEditBusinessName(e.target.value)} 
                          autoFocus 
                          className="form-control" 
                          style={{ padding: '4px 8px', fontSize: '14px' }}
                        />
                        <button onClick={handleRenameBusiness} className="modal-btn modal-btn-confirm" style={{ padding: '0 12px' }}>Salvar</button>
                        <button onClick={() => setIsEditingName(false)} className="modal-btn" style={{ padding: '0 12px' }}>✕</button>
                      </div>
                    ) : (
                      <>
                        <h3 style={{ margin: 0, color: 'var(--accent)' }}>
                          Business: {businessName} 
                          <span style={{ fontSize: '12px', marginLeft: '8px', opacity: 0.7 }}>
                            {isOwner ? '(Você é o Dono 👑)' : '(Membro)'}
                          </span>
                        </h3>
                        {isOwner && (
                          <button 
                            onClick={() => { setEditBusinessName(businessName); setIsEditingName(true); }}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', color: 'var(--text-secondary)' }}
                            title="Renomear Conta Business"
                          >
                            ✏️ Renomear
                          </button>
                        )}
                      </>

                    )}
                  </div>
                  <p style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Membros deste business compartilham as transações lançadas na visão coletiva.</p>

                  <ul style={{ listStyle: 'none', padding: 0, margin: '15px 0', border: '1px solid var(--border)', borderRadius: '8px' }}>
                    {members.map(m => (
                      <li key={m.id} style={{ padding: '10px 15px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <strong>{m.name}</strong> <br/>
                          <small style={{ color: 'var(--text-muted)' }}>{m.email}</small>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          {m.id === user.id && <span style={{ fontSize: '11px', background: 'var(--btn-secondary-bg)', padding: '4px 8px', borderRadius: '4px' }}>Você</span>}
                          
                          <button 
                            onClick={() => handleRemoveMember(m.id, m.id === user.id)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', fontSize: '18px', padding: '0 5px' }}
                            title={m.id === user.id ? 'Sair da Conta Business' : 'Remover Membro'}
                          >
                            {m.id === user.id ? '🚪' : '🗑️'}
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>

                  {isOwner && (
                    <div style={{ marginTop: '20px' }}>
                      <h4>Convidar Alguém</h4>
                      <form onSubmit={handleInvite} style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                        <input 
                          type="email" 
                          value={emailToInvite}
                          onChange={e => setEmailToInvite(e.target.value)}
                          placeholder="Digite o e-mail do usuário" 
                          className="form-control" 
                          required 
                        />
                        <button type="submit" className="modal-btn modal-btn-confirm" disabled={loading}>Convidar</button>
                      </form>
                    </div>
                  )}

                  {/* NOVO: OPÇÃO SEMPRE VISÍVEL PARA CRIAR MULTIPLAS CONTAS */}
                  <hr style={{ margin: '25px 0', border: 'none', borderTop: '1px solid var(--border)' }} />
                  <div style={{ background: 'var(--bg-primary)', padding: '15px', borderRadius: '8px', border: '1px solid var(--border)', marginTop: '20px' }}>
                    <h4>➕ Criar Conta Business Adicional</h4>
                    <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '10px' }}>Você pode gerenciar mais de uma conta business simultaneamente.</p>
                    <form onSubmit={handleCreateBusiness} style={{ display: 'flex', gap: '10px' }}>
                      <input 
                        type="text" 
                        value={newBusinessName}
                        onChange={e => setNewBusinessName(e.target.value)}
                        placeholder="Nome da Nova Conta" 
                        className="form-control" 
                        required 
                      />
                      <button type="submit" className="modal-btn modal-btn-confirm" disabled={loading}>Criar</button>
                    </form>
                  </div>

                </div>
              )}
            </div>
          )}

          {/* TAB 2: CONVITES */}
          {activeTab === 'invites' && (
            <div>
              {myInvites.length === 0 ? (
                <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '20px' }}>Você não tem convites pendentes.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {myInvites.map(inv => (
                    <div key={inv.id} style={{ background: 'var(--bg-primary)', border: '1px solid var(--border)', padding: '15px', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <strong>{inv.nome_business}</strong>
                        <p style={{ fontSize: '12px', margin: '4px 0 0', color: 'var(--text-secondary)' }}>Deseja participar desta conta business?</p>
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button onClick={() => handleRespond(inv.id, 'recusar')} disabled={loading} style={{ background: 'var(--btn-secondary-bg)', border: '1px solid var(--border)', color: 'var(--text-primary)', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer' }}>Recusar</button>
                        <button onClick={() => handleRespond(inv.id, 'aceitar')} disabled={loading} style={{ background: 'var(--accent)', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer' }}>Aceitar</button>
                      </div>
                    </div>
                  ))}
                  <div style={{ color: 'green', fontSize: '12px', marginTop: '10px' }}>
                    ✅ Aceitar um convite adiciona a conta ao seu perfil sem remover as demais.
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog 
        show={confirmRemove.show}
        title={confirmRemove.title}
        message={confirmRemove.message}
        onConfirm={executeRemoveMember}
        onCancel={() => setConfirmRemove({ ...confirmRemove, show: false, confirmWord: '' })}
        isLoading={loading}
        confirmText={confirmRemove.isSelf ? "Confirmar Saída" : "Remover"}
        type="danger"
        inputType={confirmRemove.isSelf ? "text" : "number"}
        inputValue={confirmRemove.isSelf ? confirmRemove.confirmWord : undefined}
        setInputValue={confirmRemove.isSelf ? (val) => setConfirmRemove({...confirmRemove, confirmWord: val}) : null}
        inputPlaceholder={confirmRemove.isSelf ? 'Digite "DeLeTe" para sair' : ''}
      />

    </div>
  );
};


export default BusinessManager;
